/**
 * Build/boot network resolution + fail-loud guard.
 *
 * WHY: the FE silently builds TESTNET/devnet contracts + RPC whenever VITE_NETWORK is unset
 * (feeSplitter.ts / main.tsx / wagmi.ts / useBurn.ts / useDexBridge.ts all default to testnet). Defaulting
 * to testnet is the SAFE DIRECTION — a missing var never touches mainnet by accident — but a MAINNET build
 * that silently defaulted to testnet would show Sepolia contracts + a devnet Solana RPC to a live site while
 * real wallets connect. This resolver makes that mistake LOUD instead of silent:
 *   - a PRODUCTION bundle with VITE_NETWORK UNSET → throws (caught at app boot; the caller renders a banner);
 *   - ANY unrecognized value (e.g. a "mainet" typo) → throws in dev AND prod (a wrong value must never pass).
 * A dev build with the var unset still defaults to testnet (no throw) so local flows are unchanged.
 *
 * Kept dependency-free (no `import.meta`, no React) so BOTH the Node-side vite.config.ts and the browser
 * bundle can import this one source of truth.
 */
export const PARSE_NETWORKS = ['mainnet', 'testnet'] as const;
export type ParseNetwork = (typeof PARSE_NETWORKS)[number];

/**
 * Resolve the target network from a raw VITE_NETWORK value.
 *
 * @param raw    the raw VITE_NETWORK (build var / import.meta.env), possibly undefined/empty.
 * @param isProd whether this is a production build/bundle (vite `mode === 'production'` /
 *               `import.meta.env.PROD`). Unset is tolerated (→ testnet) only when NOT production.
 * @returns 'mainnet' | 'testnet'
 * @throws  if `raw` is a non-empty value that is not a recognized network, or if it is unset in a
 *          production build (an unset network in a prod bundle would silently ship testnet to a live site).
 */
export function resolveBuildNetwork(raw: string | undefined, isProd: boolean): ParseNetwork {
  const v = (raw ?? '').trim();
  if (v === 'mainnet' || v === 'testnet') return v;
  if (v.length > 0) {
    throw new Error(
      `VITE_NETWORK="${v}" is not a recognized network (expected "mainnet" or "testnet").`,
    );
  }
  if (isProd) {
    throw new Error(
      'VITE_NETWORK must be set explicitly for a production build (e.g. VITE_NETWORK=mainnet). ' +
        'Refusing — an unset network would silently ship TESTNET contracts/RPC to a live site.',
    );
  }
  return 'testnet';
}
